import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Pencil, Trash } from "lucide-react";
import { supabase } from "@/lib/supabase";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";

// Interfaces
interface Department {
  id: string;
  name: string;
  sigla: string;
  street: string;
  number: string;
  neighborhood: string;
  zipcode: string;
  city: string;
}

interface Construction {
  id: string;
  name: string;
  address: string;
  description: string;
  status: string;
  department_id: string;
  responsible: string;
  start_date: string;
  end_date: string | null;
  department?: {
    name: string;
  };
}

interface ConstructionForm {
  id: string;
  name: string;
  department_id: string;
  responsible: string;
  address: string;
  description: string;
  status: string;
  start_date: string;
  end_date: string;
}

export default function Cadastro() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [allDepartments, setAllDepartments] = useState<Department[]>([]);
  const [constructions, setConstructions] = useState<Construction[]>([]);
  const [totalDepartments, setTotalDepartments] = useState(0);
  const [totalConstructions, setTotalConstructions] = useState(0);
  const [currentDepartmentPage, setCurrentDepartmentPage] = useState(1);
  const [currentConstructionPage, setCurrentConstructionPage] = useState(1);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [editingConstruction, setEditingConstruction] = useState<Construction | null>(null);
  const [selectedDepartmentName, setSelectedDepartmentName] = useState<string>("");
  const itemsPerPage = 5;

  // Estado inicial do formulário de construção
  const [constructionForm, setConstructionForm] = useState<ConstructionForm>({
    id: "",
    name: "",
    department_id: "",
    responsible: "",
    address: "",
    description: "",
    status: "Em Andamento",
    start_date: new Date().toISOString().split('T')[0],
    end_date: "",
  });

  // Carregar secretarias e obras
  useEffect(() => {
    loadDepartments();
    loadAllDepartments();
  }, [currentDepartmentPage]);

  const loadAllDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from("departments")
        .select("id, name, sigla, street, number, neighborhood, zipcode, city")
        .order("name");

      if (error) throw error;
      setAllDepartments(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar secretarias",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    loadConstructions();
  }, [currentConstructionPage]);

  const loadDepartments = async () => {
    try {
      // Primeiro, buscar o total de registros
      const { count, error: countError } = await supabase
        .from("departments")
        .select("*", { count: "exact" });

      if (countError) throw countError;
      setTotalDepartments(count || 0);

      // Depois, buscar os registros da página atual
      const startIndex = (currentDepartmentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage - 1;

      const { data, error } = await supabase
        .from("departments")
        .select("id, name, sigla, street, number, neighborhood, zipcode, city")
        .range(startIndex, endIndex)
        .order("name");

      if (error) throw error;
      setDepartments(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar secretarias",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const loadConstructions = async () => {
    try {
      // Primeiro, buscar o total de registros
      const { count, error: countError } = await supabase
        .from("constructions")
        .select("*", { count: "exact" });

      if (countError) throw countError;
      setTotalConstructions(count || 0);

      // Depois, buscar os registros da página atual
      const startIndex = (currentConstructionPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage - 1;

      const { data, error } = await supabase
        .from("constructions")
        .select(`
          id,
          name,
          department_id,
          responsible,
          address,
          description,
          status,
          start_date,
          end_date,
          department:departments(id, name)
        `)
        .range(startIndex, endIndex)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setConstructions(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar obras",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Estado para Secretaria
  const [departmentForm, setDepartmentForm] = useState<Department>({
    id: "",
    name: "",
    sigla: "",
    street: "",
    number: "",
    neighborhood: "",
    zipcode: "",
    city: "",
  });

  const STATUS_OPTIONS = [
    "Em Andamento",
    "Concluída",
    "Pausada",
    "Cancelada"
  ] as const;

  // Função para excluir secretaria
  const handleDeleteDepartment = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir esta secretaria?")) return;
    try {
      setLoading(true);
      const { error } = await supabase
        .from("departments")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Secretaria excluída",
        description: "A secretaria foi excluída com sucesso!",
      });
      loadDepartments();
    } catch (error: any) {
      toast({
        title: "Erro ao excluir secretaria",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para excluir obra
  const handleDeleteConstruction = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir esta obra?")) return;
    try {
      setLoading(true);
      const { error } = await supabase
        .from("constructions")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Obra excluída",
        description: "A obra foi excluída com sucesso!",
      });
      loadConstructions();
    } catch (error: any) {
      toast({
        title: "Erro ao excluir obra",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para editar secretaria
  const handleEditDepartment = (department: Department) => {
    setEditingDepartment(department);
    setDepartmentForm(department);
  };

  // Função para editar obra
  const handleEditConstruction = (construction: Construction) => {
    console.log("Dados da obra para edição:", construction);
    
    if (!construction || !construction.id) {
      console.error("Dados da obra inválidos:", construction);
      return;
    }

    const department = departments.find(d => d.id === construction.department_id);
    setSelectedDepartmentName(department?.name || "");

    setEditingConstruction(construction);
    setConstructionForm({
      id: construction.id,
      name: construction.name || "",
      department_id: construction.department_id || "",
      responsible: construction.responsible || "",
      address: construction.address || "",
      description: construction.description || "",
      status: construction.status || "Em Andamento",
      start_date: construction.start_date ? format(new Date(construction.start_date), "yyyy-MM-dd") : "",
      end_date: construction.end_date ? format(new Date(construction.end_date), "yyyy-MM-dd") : "",
    });
  };

  // Função para obter o nome da secretaria pelo ID
  const getDepartmentName = (id: string) => {
    return allDepartments.find(d => d.id === id)?.name || "";
  };

  // Função para atualizar o formulário
  const handleConstructionFormChange = (field: keyof ConstructionForm, value: string) => {
    setConstructionForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Função para cadastrar/atualizar secretaria
  const handleDepartmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);

      const departmentData = {
        name: departmentForm.name,
        sigla: departmentForm.sigla,
        street: departmentForm.street,
        number: departmentForm.number,
        neighborhood: departmentForm.neighborhood,
        zipcode: departmentForm.zipcode,
        city: departmentForm.city,
      };

      if (editingDepartment) {
        const { error } = await supabase
          .from("departments")
          .update(departmentData)
          .eq("id", editingDepartment.id);

        if (error) throw error;

        toast({
          title: "Secretaria atualizada",
          description: "A secretaria foi atualizada com sucesso!",
        });
        setEditingDepartment(null);
      } else {
        const { error } = await supabase
          .from("departments")
          .insert([departmentData]);

        if (error) throw error;

        toast({
          title: "Secretaria cadastrada",
          description: "A secretaria foi cadastrada com sucesso!",
        });
      }

      // Limpar formulário
      setDepartmentForm({
        id: "",
        name: "",
        sigla: "",
        street: "",
        number: "",
        neighborhood: "",
        zipcode: "",
        city: "",
      });
      
      // Recarregar lista
      loadDepartments();
    } catch (error: any) {
      toast({
        title: editingDepartment ? "Erro ao atualizar secretaria" : "Erro ao cadastrar secretaria",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para cadastrar/atualizar obra
  const handleConstructionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!constructionForm.name || !constructionForm.department_id || !constructionForm.responsible || !constructionForm.address || !constructionForm.start_date) {
      toast({
        title: "Erro ao salvar obra",
        description: "Por favor, preencha todos os campos obrigatórios",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      
      const constructionData = {
        name: constructionForm.name,
        department_id: constructionForm.department_id,
        responsible: constructionForm.responsible,
        address: constructionForm.address,
        description: constructionForm.description,
        status: constructionForm.status,
        start_date: constructionForm.start_date,
        ...(constructionForm.end_date ? { end_date: constructionForm.end_date } : {})
      };

      if (editingConstruction) {
        const { error } = await supabase
          .from("constructions")
          .update(constructionData)
          .eq("id", editingConstruction.id);

        if (error) throw error;

        toast({
          title: "Obra atualizada",
          description: "A obra foi atualizada com sucesso!",
        });
        setEditingConstruction(null);
      } else {
        const { error } = await supabase
          .from("constructions")
          .insert([constructionData]);

        if (error) throw error;

        toast({
          title: "Obra cadastrada",
          description: "A obra foi cadastrada com sucesso!",
        });
      }

      // Limpar formulário
      setConstructionForm({
        id: "",
        name: "",
        address: "",
        description: "",
        status: "Em Andamento",
        department_id: "",
        responsible: "",
        start_date: new Date().toISOString().split('T')[0],
        end_date: "",
      });
      
      // Recarregar lista
      loadConstructions();
    } catch (error: any) {
      toast({
        title: editingConstruction ? "Erro ao atualizar obra" : "Erro ao cadastrar obra",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4 sm:p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-2xl font-bold">Cadastro</CardTitle>
          <Building2 className="h-6 w-6 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="secretaria" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="secretaria">Secretaria</TabsTrigger>
              <TabsTrigger value="obra">Obra</TabsTrigger>
            </TabsList>

            {/* Formulário e Lista de Secretarias */}
            <TabsContent value="secretaria">
              <div className="space-y-6">
                <form onSubmit={handleDepartmentSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nome</Label>
                      <Input
                        id="name"
                        value={departmentForm.name}
                        onChange={(e) => setDepartmentForm({ ...departmentForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sigla">Sigla</Label>
                      <Input
                        id="sigla"
                        value={departmentForm.sigla}
                        onChange={(e) => setDepartmentForm({ ...departmentForm, sigla: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="street">Rua</Label>
                      <Input
                        id="street"
                        value={departmentForm.street}
                        onChange={(e) => setDepartmentForm({ ...departmentForm, street: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="number">Número</Label>
                      <Input
                        id="number"
                        value={departmentForm.number}
                        onChange={(e) => setDepartmentForm({ ...departmentForm, number: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="neighborhood">Bairro</Label>
                      <Input
                        id="neighborhood"
                        value={departmentForm.neighborhood}
                        onChange={(e) => setDepartmentForm({ ...departmentForm, neighborhood: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zipcode">CEP</Label>
                      <Input
                        id="zipcode"
                        value={departmentForm.zipcode}
                        onChange={(e) => setDepartmentForm({ ...departmentForm, zipcode: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">Cidade</Label>
                      <Input
                        id="city"
                        value={departmentForm.city}
                        onChange={(e) => setDepartmentForm({ ...departmentForm, city: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <Button type="submit" disabled={loading}>
                    {loading ? (editingDepartment ? "Atualizando..." : "Cadastrando...") : (editingDepartment ? "Atualizar Secretaria" : "Cadastrar Secretaria")}
                  </Button>
                  {editingDepartment && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setEditingDepartment(null);
                        setDepartmentForm({
                          id: "",
                          name: "",
                          sigla: "",
                          street: "",
                          number: "",
                          neighborhood: "",
                          zipcode: "",
                          city: "",
                        });
                      }}
                    >
                      Cancelar Edição
                    </Button>
                  )}
                </form>

                {/* Lista de Secretarias */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">
                      Total de Secretarias: {totalDepartments}
                    </h3>
                  </div>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead>Sigla</TableHead>
                          <TableHead>Endereço</TableHead>
                          <TableHead>Cidade</TableHead>
                          <TableHead className="w-[100px]">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {departments.map((department) => (
                          <TableRow key={department.id}>
                            <TableCell>{department.name}</TableCell>
                            <TableCell>{department.sigla}</TableCell>
                            <TableCell>{`${department.street}, ${department.number}`}</TableCell>
                            <TableCell>{department.city}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditDepartment(department)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteDepartment(department.id)}
                                >
                                  <Trash className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Paginação de Secretarias */}
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentDepartmentPage(page => Math.max(1, page - 1))}
                      disabled={currentDepartmentPage === 1}
                    >
                      Anterior
                    </Button>
                    <span className="text-sm">
                      Página {currentDepartmentPage} de {Math.ceil(totalDepartments / itemsPerPage)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentDepartmentPage(page => page + 1)}
                      disabled={currentDepartmentPage >= Math.ceil(totalDepartments / itemsPerPage)}
                    >
                      Próxima
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Formulário e Lista de Obras */}
            <TabsContent value="obra">
              <div className="space-y-6">
                <form onSubmit={handleConstructionSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="construction-name">Nome da Obra</Label>
                      <Input
                        id="construction-name"
                        value={constructionForm.name}
                        onChange={(e) => handleConstructionFormChange("name", e.target.value)}
                        placeholder="Digite o nome da obra"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="construction-department">Secretaria</Label>
                      <div className="relative">
                        <select
                          id="construction-department"
                          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          value={constructionForm.department_id}
                          onChange={(e) => handleConstructionFormChange("department_id", e.target.value)}
                        >
                          <option value="">Selecione uma secretaria</option>
                          {allDepartments.map((department) => (
                            <option key={department.id} value={department.id}>
                              {department.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {constructionForm.department_id && (
                        <p className="text-sm text-muted-foreground">
                          Secretaria selecionada: {getDepartmentName(constructionForm.department_id)}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="construction-responsible">Responsável</Label>
                      <Input
                        id="construction-responsible"
                        value={constructionForm.responsible}
                        onChange={(e) => handleConstructionFormChange("responsible", e.target.value)}
                        placeholder="Digite o nome do responsável"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="construction-address">Endereço</Label>
                      <Input
                        id="construction-address"
                        value={constructionForm.address}
                        onChange={(e) => handleConstructionFormChange("address", e.target.value)}
                        placeholder="Digite o endereço da obra"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="construction-description">Descrição</Label>
                      <Textarea
                        id="construction-description"
                        value={constructionForm.description}
                        onChange={(e) => handleConstructionFormChange("description", e.target.value)}
                        placeholder="Digite uma descrição para a obra"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="construction-status">Status</Label>
                      <select
                        id="construction-status"
                        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        value={constructionForm.status}
                        onChange={(e) => handleConstructionFormChange("status", e.target.value)}
                      >
                        <option value="Em Andamento">Em Andamento</option>
                        <option value="Concluída">Concluída</option>
                        <option value="Paralisada">Paralisada</option>
                        <option value="Cancelada">Cancelada</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="construction-start-date">Data de Início</Label>
                      <Input
                        id="construction-start-date"
                        type="date"
                        value={constructionForm.start_date}
                        onChange={(e) => handleConstructionFormChange("start_date", e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="construction-end-date">Data de Término</Label>
                      <Input
                        id="construction-end-date"
                        type="date"
                        value={constructionForm.end_date}
                        onChange={(e) => handleConstructionFormChange("end_date", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button type="submit" disabled={loading}>
                      {loading ? (editingConstruction ? "Atualizando..." : "Cadastrando...") : (editingConstruction ? "Atualizar Obra" : "Cadastrar Obra")}
                    </Button>
                    {editingConstruction && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setEditingConstruction(null);
                          setConstructionForm({
                            id: "",
                            name: "",
                            department_id: "",
                            responsible: "",
                            address: "",
                            description: "",
                            status: "Em Andamento",
                            start_date: new Date().toISOString().split('T')[0],
                            end_date: "",
                          });
                        }}
                      >
                        Cancelar Edição
                      </Button>
                    )}
                  </div>
                </form>

                {/* Lista de Obras */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">
                      Total de Obras: {totalConstructions}
                    </h3>
                  </div>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead>Secretaria</TableHead>
                          <TableHead>Responsável</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Data Início</TableHead>
                          <TableHead className="w-[100px]">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {constructions.map((construction) => (
                          <TableRow key={construction.id}>
                            <TableCell>{construction.name}</TableCell>
                            <TableCell>{construction.department?.name}</TableCell>
                            <TableCell>{construction.responsible}</TableCell>
                            <TableCell>{construction.status}</TableCell>
                            <TableCell>
                              {format(new Date(construction.start_date), "dd/MM/yyyy", { locale: ptBR })}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditConstruction(construction)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteConstruction(construction.id)}
                                >
                                  <Trash className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Paginação de Obras */}
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentConstructionPage(page => Math.max(1, page - 1))}
                      disabled={currentConstructionPage === 1}
                    >
                      Anterior
                    </Button>
                    <span className="text-sm">
                      Página {currentConstructionPage} de {Math.ceil(totalConstructions / itemsPerPage)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentConstructionPage(page => page + 1)}
                      disabled={currentConstructionPage >= Math.ceil(totalConstructions / itemsPerPage)}
                    >
                      Próxima
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
